import { useQuery } from "@tanstack/react-query";
import { Compass, Search, Link2, FileDown } from "lucide-react";
import { api } from "@/api/client";
import { useAuthStore } from "@/store/authSlice";
import type { JenisPNBP } from "@/types/document";

interface CompassData {
  jenis_pnbp?: { id: string; kode: string; nama: string };
  tahun: number;
  landasan_hukum: { judul: string; nomor_dokumen?: string; bentuk_dokumen_nama: string; status_nama: string; is_active: boolean }[];
  stakeholder: { pic_nama?: string; pic_jabatan?: string; unit_kerja?: string; stakeholder_eksternal: string[]; hierarki: string[] };
  siklus_progress: { kode: string; nama: string; uploaded_count: number; completion_pct: number; status_color: string }[];
  sop_list: { judul: string; nomor_dokumen?: string; status_nama: string }[];
  total_documents: number;
  approved_count: number;
  pending_count: number;
  approved_pct: number;
  by_status: { kode: string; nama: string; count: number }[];
  expiring_soon: number;
  expiry_alerts: { judul: string; jenis_pnbp_nama: string; days_remaining: number }[];
  knowledge_items: { judul: string; total_views: number; kategori: string }[];
}

interface CompassHeaderProps {
  selectedJenisId: string;
  selectedTahun: number;
  searchQuery: string;
  onJenisChange: (id: string) => void;
  onTahunChange: (tahun: number) => void;
  onSearchChange: (q: string) => void;
  compassData?: CompassData;
}

export default function CompassHeader({
  selectedJenisId,
  selectedTahun,
  searchQuery,
  onJenisChange,
  onTahunChange,
  onSearchChange,
  compassData,
}: CompassHeaderProps) {
  const user = useAuthStore((s) => s.user);

  const { data: jenisList = [] } = useQuery<JenisPNBP[]>({
    queryKey: ["master", "jenis-pnbp"],
    queryFn: () => api.get("/master/jenis-pnbp"),
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
  };

  const handleExportPDF = () => {
    if (!compassData) return;

    const d = compassData;
    const jenisLabel = d.jenis_pnbp ? `${d.jenis_pnbp.nama} (${d.jenis_pnbp.kode})` : "Semua Jenis PNBP";
    const now = new Date().toLocaleString("id-ID", {
      day: "numeric", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });

    const html = `<!DOCTYPE html>
<html lang="id"><head><meta charset="UTF-8">
<title>Ringkasan Eksekutif PNBP COMPASS</title>
<style>
  body{font-family:system-ui,-apple-system,sans-serif;margin:40px;color:#1e293b;font-size:13px;line-height:1.6}
  h1{font-size:20px;color:#1e40af;margin-bottom:4px}
  h2{font-size:15px;color:#334155;border-bottom:2px solid #3b82f6;padding-bottom:4px;margin-top:28px}
  .subtitle{color:#64748b;font-size:12px;margin-bottom:24px}
  .grid{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:12px;margin:16px 0}
  .card{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px;text-align:center}
  .card .value{font-size:22px;font-weight:700;color:#1e40af}
  .card .label{font-size:11px;color:#64748b;margin-top:2px}
  table{width:100%;border-collapse:collapse;margin:8px 0;font-size:12px}
  th{background:#f1f5f9;text-align:left;padding:6px 10px;font-weight:600;border:1px solid #e2e8f0}
  td{padding:6px 10px;border:1px solid #e2e8f0}
  .badge{display:inline-block;padding:2px 8px;border-radius:9999px;font-size:10px;font-weight:600}
  .badge-green{background:#dcfce7;color:#166534}
  .badge-red{background:#fee2e2;color:#991b1b}
  .badge-yellow{background:#fef9c3;color:#854d0e}
  .footer{margin-top:32px;padding-top:12px;border-top:1px solid #e2e8f0;font-size:11px;color:#94a3b8;text-align:center}
  @media print{body{margin:20px}h1{font-size:18px}}
</style></head><body>
<h1>Ringkasan Eksekutif — PNBP COMPASS</h1>
<div class="subtitle">${jenisLabel} &bull; Tahun ${d.tahun} &bull; Diekspor: ${now} WIB &bull; Oleh: ${user?.username || "-"}</div>

<h2>Ikhtisar Kinerja</h2>
<div class="grid">
  <div class="card"><div class="value">${d.total_documents}</div><div class="label">Total Dokumen</div></div>
  <div class="card"><div class="value">${d.approved_count}</div><div class="label">Disetujui</div></div>
  <div class="card"><div class="value">${d.pending_count}</div><div class="label">Menunggu Persetujuan</div></div>
  <div class="card"><div class="value">${d.expiring_soon}</div><div class="label">Segera Kedaluwarsa</div></div>
</div>
<p>Tingkat persetujuan: <strong>${d.approved_pct.toFixed(1)}%</strong></p>

${d.by_status.length ? `<h2>Distribusi Status Dokumen</h2>
<table><thead><tr><th>Status</th><th>Jumlah</th></tr></thead><tbody>
${d.by_status.map(s => `<tr><td>${s.nama}</td><td>${s.count}</td></tr>`).join("")}
</tbody></table>` : ""}

${d.landasan_hukum.length ? `<h2>Landasan Hukum (Regulasi)</h2>
<table><thead><tr><th>No</th><th>Judul</th><th>Nomor</th><th>Bentuk</th><th>Status</th><th>Aktif</th></tr></thead><tbody>
${d.landasan_hukum.map((l, i) => `<tr><td>${i + 1}</td><td>${l.judul}</td><td>${l.nomor_dokumen || "-"}</td><td>${l.bentuk_dokumen_nama}</td><td>${l.status_nama}</td><td><span class="badge ${l.is_active ? "badge-green" : "badge-red"}">${l.is_active ? "Aktif" : "Tidak Aktif"}</span></td></tr>`).join("")}
</tbody></table>` : ""}

${d.stakeholder.pic_nama || d.stakeholder.unit_kerja ? `<h2>Stakeholder</h2>
<table><tbody>
${d.stakeholder.pic_nama ? `<tr><td><strong>PIC</strong></td><td>${d.stakeholder.pic_nama}${d.stakeholder.pic_jabatan ? ` — ${d.stakeholder.pic_jabatan}` : ""}</td></tr>` : ""}
${d.stakeholder.unit_kerja ? `<tr><td><strong>Unit Kerja</strong></td><td>${d.stakeholder.unit_kerja}</td></tr>` : ""}
${d.stakeholder.hierarki.length ? `<tr><td><strong>Hierarki</strong></td><td>${d.stakeholder.hierarki.join(" → ")}</td></tr>` : ""}
${d.stakeholder.stakeholder_eksternal.length ? `<tr><td><strong>Eksternal</strong></td><td>${d.stakeholder.stakeholder_eksternal.join(", ")}</td></tr>` : ""}
</tbody></table>` : ""}

${d.siklus_progress.length ? `<h2>Progress per Siklus Keuangan</h2>
<table><thead><tr><th>Siklus</th><th>Dokumen</th><th>Kelengkapan</th><th>Status</th></tr></thead><tbody>
${d.siklus_progress.map(s => `<tr><td>${s.nama}</td><td>${s.uploaded_count}</td><td>${s.completion_pct.toFixed(0)}%</td><td><span class="badge badge-${s.status_color}">${s.status_color === "green" ? "Baik" : s.status_color === "yellow" ? "Perlu Perhatian" : "Kritis"}</span></td></tr>`).join("")}
</tbody></table>` : ""}

${d.sop_list.length ? `<h2>SOP & Juknis</h2>
<table><thead><tr><th>No</th><th>Judul</th><th>Nomor</th><th>Status</th></tr></thead><tbody>
${d.sop_list.map((s, i) => `<tr><td>${i + 1}</td><td>${s.judul}</td><td>${s.nomor_dokumen || "-"}</td><td>${s.status_nama}</td></tr>`).join("")}
</tbody></table>` : ""}

${d.expiry_alerts.length ? `<h2>Peringatan Kedaluwarsa (30 Hari)</h2>
<table><thead><tr><th>Judul</th><th>Jenis PNBP</th><th>Sisa Hari</th></tr></thead><tbody>
${d.expiry_alerts.map(a => `<tr><td>${a.judul}</td><td>${a.jenis_pnbp_nama}</td><td><span class="badge ${a.days_remaining <= 7 ? "badge-red" : "badge-yellow"}">${a.days_remaining} hari</span></td></tr>`).join("")}
</tbody></table>` : ""}

${d.knowledge_items.length ? `<h2>Knowledge Sharing</h2>
<table><thead><tr><th>Judul</th><th>Views</th><th>Kategori</th></tr></thead><tbody>
${d.knowledge_items.map(k => `<tr><td>${k.judul}</td><td>${k.total_views}</td><td>${k.kategori === "populer" ? "Populer" : "Best Practice"}</td></tr>`).join("")}
</tbody></table>` : ""}

<div class="footer">Dokumen ini digenerate otomatis oleh PNBP COMPASS — Sistem Manajemen Pengetahuan PNBP</div>
</body></html>`;

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => printWindow.print();
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="flex items-center gap-2.5">
          <Compass className="h-6 w-6 text-white" />
          <div>
            <h1 className="text-lg font-bold text-white leading-tight">PNBP COMPASS</h1>
            <p className="text-xs text-blue-200">Dashboard Pemahaman Komprehensif per Jenis PNBP</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-blue-200">
            Terakhir diperbarui: {new Date().toLocaleString("id-ID", {
              day: "numeric",
              month: "long",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })} WIB
          </span>
          <span className="text-sm font-medium text-white">{user?.username}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-t border-blue-500/20">
        {/* Jenis PNBP */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Jenis PNBP:</span>
          <select
            value={selectedJenisId}
            onChange={(e) => onJenisChange(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Semua Jenis</option>
            {jenisList.map((j) => (
              <option key={j.id} value={j.id}>
                {j.nama} ({j.kode})
              </option>
            ))}
          </select>
        </div>

        {/* Tahun */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tahun:</span>
          <select
            value={selectedTahun}
            onChange={(e) => onTahunChange(Number(e.target.value))}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          >
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Cari dokumen..."
            className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={handleExportPDF}
            disabled={!compassData}
            className="flex items-center gap-1.5 rounded-lg border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FileDown className="h-3.5 w-3.5" />
            Ekspor Ringkasan Eksekutif (PDF)
          </button>
          <button
            onClick={handleCopyLink}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <Link2 className="h-3.5 w-3.5" />
            Salin Link
          </button>
        </div>
      </div>
    </div>
  );
}
